import React, { useEffect, useRef, useState, useCallback } from 'react';
import ProfilePreview from '../ProfilePreview';
import { ProfileResponse } from '../../../utils/types';
import JWButton from '../../brand/JWButton';
import { boostProfile } from '../../../utils/paymentUtil';
import { LightningIcon } from '../../icons';
import PaymentSelectModal from '../../modals/PaymentSelectModal';
import { debounce } from '../../../utils/helpers';
import PromoContent from '../../Promotions';
import { shouldShowInterspersedPromo } from '../../../config/promotions';
import { TrendingUp } from 'lucide-react';
import ViewHighlightsModal from '@/components/modals/ViewHighlightsModalLazy';
import { HighlightType } from '@/types/profile';
import Loader from '@/components/ui/loader';
import { cn } from '@/lib/utils';
import ViewGridIcon from '@/assets/icons/ViewGridIcon';
import ViewListIcon from '@/assets/icons/ViewListIcon';
import RotatingSubtitle from '../RotatingSubtitle';
import { toast } from 'react-toastify';
import SortControls from '../../common/SortControls';
import { SortOption } from '../../../api/profile';
import { resetScrollPositions } from '../../../utils/scrollUtils';
import { useQuery } from '@tanstack/react-query';
import { fetchJamBoardPins } from '../../../api/jamboard';
import { JamBoardPin } from '@/types/jamboard';
import { PAGE_PANEL_CLASS } from '@/theme/surfaceClasses';

interface DiscoverMainSectionProps {
    profiles: ProfileResponse[] | null
    onSortChange: (sortBy: SortOption) => void
    isLoading: boolean
    loadMore: () => void
    hasMore: boolean
    isFetchingMore: boolean
    currentViewMode?: 'list' | 'grid'
    onViewModeChange?: (newViewMode: 'list' | 'grid') => void
    isMobile?: boolean
    currentSortBy?: SortOption
}

const DiscoverMainSection = ({
    profiles,
    onSortChange,
    isLoading,
    loadMore,
    hasMore,
    isFetchingMore,
    currentViewMode,
    onViewModeChange,
    isMobile: propIsMobile,
    currentSortBy = 'relevance'
}: DiscoverMainSectionProps) => {
    const observerTarget = useRef(null);
    const contentContainerRef = useRef<HTMLDivElement>(null);
    const [localViewMode, setLocalViewMode] = useState<'list' | 'grid'>(() => {
        // Only use this if currentViewMode is not provided from props
        if (currentViewMode) return currentViewMode;
        // Try to get saved preference from localStorage, default to 'list'
        const savedViewMode = localStorage.getItem('discover_view_mode');
        return (savedViewMode === 'list' || savedViewMode === 'grid') ? savedViewMode : 'list';
    });
    const [localIsMobile, setLocalIsMobile] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedHighlight, setSelectedHighlight] = useState<HighlightType | null>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
    const [selectedProfileHighlights, setSelectedProfileHighlights] = useState<HighlightType[]>([]);
    const { data: allJamBoardPins = [] } = useQuery({
        queryKey: ['jamboard', 'pins'],
        queryFn: fetchJamBoardPins,
    });
    // Initialize with parent's currentSortBy prop
    const [currentSort, setCurrentSort] = useState<SortOption>(
        currentSortBy === 'recent' ? 'recent' : 'relevance'
    );

    // Keep currentSort in sync with parent's currentSortBy prop and handle scroll reset
    useEffect(() => {
        if (currentSortBy === 'recent' || currentSortBy === 'relevance') {
            // Only update if different to avoid unnecessary re-renders
            if (currentSort !== currentSortBy) {
                setCurrentSort(currentSortBy as 'relevance' | 'recent');

                // Use the utility function to reset all scroll positions
                setTimeout(() => {
                    resetScrollPositions();
                }, 50); // Small delay to ensure DOM is updated
            }
        }
    }, [currentSortBy, currentSort]);

    // Use props if provided, otherwise use local state
    const isMobile = propIsMobile !== undefined ? propIsMobile : localIsMobile;
    const viewMode = currentViewMode !== undefined ? currentViewMode : localViewMode;

    // Check if we're on a mobile device (only if prop isn't provided)
    useEffect(() => {
        if (propIsMobile !== undefined) return;

        const checkIfMobile = () => {
            setLocalIsMobile(window.innerWidth < 768); // Standard breakpoint for mobile
        };

        // Initial check
        checkIfMobile();

        // Add debounced event listener for window resize
        const debouncedCheckIfMobile = debounce(checkIfMobile, 250);
        window.addEventListener('resize', debouncedCheckIfMobile);

        // Cleanup
        return () => window.removeEventListener('resize', debouncedCheckIfMobile);
    }, [propIsMobile]);

    // Add a counter to track observer reconnections
    const observerReconnectCountRef = useRef(0);

    // Observer setup - always reconnect when dependencies change
    useEffect(() => {
        // Don't set up observer for recent sort - we don't want infinite scroll
        if (!observerTarget.current || !hasMore || currentSort === 'recent') return;

        observerReconnectCountRef.current += 1;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];

                if (entry.isIntersecting && hasMore && !isFetchingMore) {
                    loadMore();
                }
            },
            {
                // Use a very low threshold to make it easier to trigger
                threshold: 0.01,
                // Large margin to detect way before fully visible
                rootMargin: '200px 0px'
            }
        );

        observer.observe(observerTarget.current);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, isFetchingMore, loadMore, currentSort]);

    // Calculate effective view mode (always list on mobile)
    const effectiveViewMode = isMobile ? 'list' : viewMode;

    const toggleViewMode = useCallback(() => {
        const newMode = viewMode === 'list' ? 'grid' : 'list';

        // If we have an external handler, use it
        if (onViewModeChange) {
            onViewModeChange(newMode);
        } else {
            // Otherwise manage locally
            setLocalViewMode(newMode);
        }

        // Save to localStorage for persistence
        localStorage.setItem('discover_view_mode', newMode);
    }, [viewMode, onViewModeChange]);

    // Handle sort change from desktop dropdown
    const handleSortChange = useCallback((value: SortOption) => {
        // Update local state
        setCurrentSort(value);
        // Call parent handler
        onSortChange(value);
    }, [onSortChange]);

    const handleBoostProfile = useCallback(() => {
        setShowPaymentModal(true);
    }, []);

    const handlePaymentSelect = useCallback(async (paymentType: string) => {
        await boostProfile(paymentType);
        setShowPaymentModal(false);
    }, []);

    const handleHighlightClick = useCallback((
        highlight: HighlightType,
        index: number,
        profileId: string,
        highlights: HighlightType[]
    ) => {
        setSelectedProfileId(profileId);
        setSelectedHighlight(highlight);
        setSelectedIndex(index);
        setSelectedProfileHighlights(highlights || []);
        setShowViewModal(true);
    }, []);

    const handleNavigate = useCallback((direction: 'prev' | 'next') => {
        if (!selectedProfileHighlights.length) return;

        const newIndex = direction === 'prev'
            ? (selectedIndex - 1 + selectedProfileHighlights.length) % selectedProfileHighlights.length
            : (selectedIndex + 1) % selectedProfileHighlights.length;

        setSelectedIndex(newIndex);
        setSelectedHighlight(selectedProfileHighlights[newIndex] as HighlightType);
    }, [selectedIndex, selectedProfileHighlights]);

    const selectedProfile = selectedProfileId
        ? profiles?.find((p) => p._id === selectedProfileId)
        : null;

    const selectedProfileJamBoardPins = selectedProfile?.userId
        ? allJamBoardPins.filter(
            (pin: JamBoardPin) =>
                String(pin.author.userId) === String(selectedProfile.userId)
        )
        : [];

    // Handle switching from "recent" to "relevance" sort
    const handleSwitchToRelevance = useCallback(() => {
        setCurrentSort('relevance');
        onSortChange('relevance');
        toast.success('Switched to Relevance sorting', {
            position: 'bottom-center',
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
    }, [onSortChange]);

    const renderLoadingState = useCallback(() => (
        <div className="flex items-center justify-center h-full w-full pt-4">
            <Loader size="lg" text="Finding your next collaborator..." />
        </div>
    ), []);

    const renderEmptyState = useCallback(() => (
        <div className="text-center font-sans text-xl font-bold my-8 px-4">
            <div className="bg-gray-50 p-8 rounded-lg">
                <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>No profiles found matching your criteria.</p>
                <p className="text-sm text-gray-500 mt-2">Try adjusting your search filters or check back later!</p>
            </div>
        </div>
    ), []);

    const renderProfiles = useCallback(() => (
        <div className={effectiveViewMode === 'grid'
            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-3 auto-rows-[180px] overflow-visible'
            : isMobile ? 'flex flex-col w-full p-2 mobile-scroll-container' : 'profile-preview-list'
        }>
            <div className={effectiveViewMode === 'list' && !isMobile ? 'profile-preview-list__grid' : 'contents'}>
            {profiles?.map((profile, i) => (
                <React.Fragment key={`${profile._id}-${i}`}>
                    <div
                        className={effectiveViewMode === 'grid'
                            ? 'h-[180px] w-full overflow-visible profile-fade-in profile-item'
                            : isMobile ? 'w-full overflow-hidden profile-fade-in profile-item' : 'profile-preview-list__item profile-fade-in profile-item'
                        }
                        style={{
                            animationDelay: `${Math.min(i * 0.05, 0.5)}s`
                        }}
                    >
                        <ProfilePreview
                            profile={profile}
                            isGridView={effectiveViewMode === 'grid'}
                            onHighlightClick={handleHighlightClick}
                            profileIndex={i}
                        />
                        {effectiveViewMode === 'list' && i !== (profiles?.length || 0) - 1 && (
                            <div className={isMobile
                                ? 'h-[2px] w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent my-2'
                                : 'profile-preview-list__rule'}
                            />
                        )}
                    </div>

                    {/* Show interspersed ad after every INTERSPERSED_PROMO_PAGES profiles */}
                    {isMobile &&
                     shouldShowInterspersedPromo('discover', i, profiles?.length || 0) && (
                        <div className="my-4">
                            <PromoContent pageName="discover" />
                        </div>
                    )}
                </React.Fragment>
            ))}

            {/* Observer target and loading indicator - positioned to be easily visible */}
            <div
                ref={observerTarget}
                className="profile-preview-list__span h-40 flex items-center justify-center col-span-full my-6 loading-more-container bg-gray-50 rounded-lg border border-gray-200 shadow-sm"
                style={{ minHeight: '160px', scrollMargin: '100px' }}
            >
                {isFetchingMore ? (
                    <div className="flex flex-col items-center p-4 w-full">
                        <Loader size="md" />
                        <p className="text-sm font-medium text-gray-600 mt-3">Loading more profiles...</p>
                        <p className="text-xs text-gray-500 mt-1">Please wait while we find matches for you</p>
                    </div>
                ) : currentSort === 'recent' ? (
                    <div className="flex flex-col items-center p-4 w-full">
                        <p className="text-sm font-medium text-gray-600 mb-2">You've reached the end of recent profiles</p>
                        <p className="text-xs text-gray-500 mb-3">Showing 50 recent profiles. Switch to relevance sorting for more profiles.</p>
                        <JWButton
                            onClick={handleSwitchToRelevance}
                            variant="default"
                            size="medium"
                            className="mt-1"
                        >
                            <TrendingUp className="w-4 h-4 mr-1" />
                            Switch to Relevance
                        </JWButton>
                    </div>
                ) : hasMore ? (
                    <div className="h-20 flex flex-col items-center justify-center">
                        <p className="text-sm font-medium text-gray-600">Scroll down to load more profiles</p>
                        <p className="text-xs text-gray-500 mt-1 mb-2">We have more potential matches for you</p>
                        <div className="mt-2 animate-bounce">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                                <path d="M12 5v14M19 12l-7 7-7-7"/>
                            </svg>
                        </div>
                    </div>
                ) : (
                    <div className="h-16 flex items-center justify-center text-sm text-gray-600">
                        You've seen all available profiles
                    </div>
                )}
            </div>
            </div>
        </div>
    ), [profiles, effectiveViewMode, isMobile, handleHighlightClick, isFetchingMore, hasMore, currentSort, handleSwitchToRelevance]);

    return (
        <div className={`profile-preview-host ${PAGE_PANEL_CLASS} overflow-visible overflow-x-hidden`}>
            <PaymentSelectModal
                show={showPaymentModal}
                setShow={setShowPaymentModal}
                onPaymentSelect={handlePaymentSelect}
                onClose={() => setShowPaymentModal(false)}
            />

            {/* Conditionally render mobile or desktop header */}
            {isMobile ? (
                <div className="flex flex-col p-3 border-b">
                    {/* Mobile header with drop-down interaction */}
                    <div className="flex justify-between items-center w-full">
                        {/* Title on the left */}
                        <h1 className="text-eerieblack text-lg font-bold whitespace-nowrap mobile-text-optimize">
                            Discover<RotatingSubtitle isLoading={isLoading || isFetchingMore} />
                        </h1>
                        {/* Icons grouped on the right */}
                        <div className="flex items-center space-x-2">
                            {/* Use shared SortControls */}
                            <SortControls
                                currentSort={currentSort}
                                onChange={handleSortChange}
                                isMobile={true}
                            />
                            {/* Mobile boost profile button */}
                            <button
                                className="flex items-center justify-center p-1 h-9 rounded-md transition-colors mobile-touch-target xl:hidden bg-white border border-gray-200 hover:bg-gray-50 min-h-[40px] focus:outline-none"
                                onClick={handleBoostProfile}
                                aria-label="Boost profile"
                            >
                                <div className="text-gray-500 flex items-center justify-center">
                                    <LightningIcon />
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                /* Desktop header with original structure */
                <div className="p-3 py-2.5 border-b flex flex-col sm:flex-row justify-between items-center space-y-1.5 sm:space-y-0">
                    <h1 className="text-eerieblack text-xl font-bold">
                        Discover<RotatingSubtitle isLoading={isLoading || isFetchingMore} />
                    </h1>
                    <div className="flex items-center space-x-2">
                        {/* Use shared SortControls */}
                        <SortControls
                            currentSort={currentSort}
                            onChange={handleSortChange}
                            isMobile={false}
                        />
                        {/* Boost button for all views except desktop */}
                        <button
                            className="flex items-center justify-center p-1 h-9 rounded-md transition-colors mobile-touch-target xl:hidden bg-white border border-gray-200 hover:bg-gray-50 min-h-[40px] min-w-[40px] focus:outline-none"
                            onClick={handleBoostProfile}
                            aria-label="Boost profile"
                        >
                            <div className="text-gray-500">
                                <LightningIcon />
                            </div>
                        </button>
                        {/* Toggle button for desktop */}
                        <button
                            onClick={toggleViewMode}
                            className="flex items-center justify-center p-1 h-9 rounded-md transition-colors bg-white border border-gray-200 hover:bg-gray-50 min-h-[40px] min-w-[40px] focus:outline-none"
                            title={viewMode === 'list' ? 'Switch to grid view' : 'Switch to list view'}
                        >
                            {viewMode === 'list' ? (
                                <ViewGridIcon />
                            ) : (
                                <ViewListIcon />
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Use key prop to force complete remount when sort changes */}
            <div
                key={`content-${currentSort}`}
                id="discover-content-container"
                ref={contentContainerRef}
                className={cn(
                    'profile-preview-body min-h-0 flex-1 rounded-b-lg',
                    isMobile ? 'overflow-visible mobile-pull-indicator overflow-x-hidden' : 'overflow-y-auto overflow-x-hidden content-container'
                )}
            >
                {isLoading && (!profiles || profiles.length === 0) ? renderLoadingState() :
                    profiles && profiles.length > 0 ? renderProfiles() : renderEmptyState()}
            </div>

            {/* Calculate if navigation buttons should be shown */}
            {(() => {
                const hasMultipleHighlights = selectedProfileHighlights.length > 1;

                return (
                    <ViewHighlightsModal
                        show={showViewModal}
                        setShow={setShowViewModal}
                        highlight={selectedHighlight}
                        onClose={() => setShowViewModal(false)}
                        onNavigate={handleNavigate}
                        canNavigatePrev={hasMultipleHighlights}
                        canNavigateNext={hasMultipleHighlights}
                        isOwnHighlight={false}
                        activeJamBoardPins={selectedProfileJamBoardPins}
                        canApplyToJamBoardPins={true}
                    />
                );
            })()}
        </div>
    );
};

export default DiscoverMainSection;
